import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  UserGroupIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  ArrowTopRightOnSquareIcon,
  PlayIcon,
  StopIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { crewApi } from "../../../api/crew";
import { useCompanyHasModule } from "../../../routes/roleOnly";

/**
 * Crew / shift roster for a project (API /api/v1/crew/*).
 * Requires company feature module `crew_scheduling`.
 */
export function CrewProjectPanel({ projectId, selectedDate }) {
  const { t } = useTranslation();
  const crewEnabled = useCompanyHasModule("crew_scheduling");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dateStr =
    typeof selectedDate === "string"
      ? selectedDate
      : selectedDate?.toISOString?.()?.substring(0, 10) ||
        new Date().toISOString().substring(0, 10);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await crewApi.roster({ project_id: projectId, date: dateStr });
      setData(res.data?.data || null);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, dateStr]);

  useEffect(() => {
    load();
  }, [load]);

  const seedChecklists = async () => {
    try {
      await crewApi.seedChecklistTemplates();
      toast.success(t("crew.toast.checklists_seeded"));
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const clockIn = async (shift) => {
    try {
      await crewApi.clockIn({
        shift_id: shift?.id,
        project_id: shift?.project_id || projectId,
        clock_in_at: new Date().toISOString(),
        source: "web",
      });
      toast.success(t("crew.toast.clocked_in"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const clockOut = async (punch) => {
    try {
      const res = await crewApi.clockOut(punch.id, {
        clock_out_at: new Date().toISOString(),
        break_minutes: punch.break_minutes || 0,
      });
      if (res.data?.hour_error) toast.error(res.data.hour_error);
      else toast.success(t("crew.toast.clocked_out"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  if (!crewEnabled) return null;

  if (!projectId) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-white/60 p-4 text-sm text-gray-500">
        {t("crew.panel.no_project")}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
            <UserGroupIcon className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">{t("crew.panel.title")}</h3>
            <p className="text-xs text-gray-500">{t("crew.panel.subtitle")}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 sm:pt-0">
          <Link
            to={`/dashboard/projects/crew?project_id=${projectId}&date=${dateStr}`}
            className="inline-flex items-center rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-sky-700"
          >
            {t("crew.panel.open_full")}
          </Link>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {loading ? "…" : t("project.plan.loading")}
          </button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {error && (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t("crew.panel.error")}: {error}
          </div>
        )}

        {loading && !data && (
          <p className="text-sm text-gray-500">{t("crew.panel.loading")}</p>
        )}

        {data && (
          <>
            <section>
              <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                <ClockIcon className="h-4 w-4 text-gray-500" />
                {t("crew.panel.shifts")} ({data.shifts?.length || 0})
              </h4>
              <ul className="space-y-2">
                {(data.shifts || []).map((s) => (
                  <li
                    key={s.id}
                    className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2 text-sm text-gray-800"
                  >
                    <div className="font-medium">{s.title}</div>
                    <div className="text-xs text-gray-600">
                      {s.starts_at?.replace("T", " ").slice(0, 16)} –{" "}
                      {s.ends_at?.replace("T", " ").slice(0, 16)}
                      {s.status ? ` · ${s.status}` : ""}
                    </div>
                    {(s.assignments || []).length > 0 && (
                      <ul className="mt-1 text-xs text-gray-600">
                        {s.assignments.map((a) => (
                          <li key={a.id}>
                            {(a.user && (a.user.displayTitle || a.user.email)) || a.user_id} — {a.status}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => clockIn(s)}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-white px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                      >
                        <PlayIcon className="h-3.5 w-3.5" />
                        {t("crew.panel.clock_in")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h4 className="mb-2 text-sm font-medium text-gray-800">{t("crew.panel.absences")}</h4>
              <ul className="text-sm text-gray-700">
                {(data.absences || []).map((a) => (
                  <li key={`${a.user_id}-${a.date_from}`}>
                    User {a.user_id}: {a.date_from} – {a.date_too}
                  </li>
                ))}
                {(data.absences || []).length === 0 && (
                  <li className="text-gray-400">—</li>
                )}
              </ul>
            </section>

            <section>
              <h4 className="mb-2 text-sm font-medium text-gray-800">{t("crew.panel.open_punches")}</h4>
              <ul className="text-sm text-gray-700">
                {(data.open_punches || []).map((p) => (
                  <li key={p.id} className="flex flex-col gap-2 rounded-lg border border-gray-100 p-2 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      #{p.id} user {p.user_id} · in {p.clock_in_at}
                    </span>
                    <button
                      type="button"
                      onClick={() => clockOut(p)}
                      className="inline-flex items-center justify-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                    >
                      <StopIcon className="h-3.5 w-3.5" />
                      {t("crew.panel.clock_out")}
                    </button>
                  </li>
                ))}
                {(data.open_punches || []).length === 0 && (
                  <li className="text-gray-400">—</li>
                )}
              </ul>
            </section>

            <div className="flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:flex-wrap">
              <Link
                to={`/dashboard/hours?view=week&date=${dateStr}`}
                className="inline-flex items-center justify-center gap-1 rounded-md bg-sky-600 px-3 py-2 text-xs font-medium text-white shadow hover:bg-sky-700 sm:flex-1"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                {t("crew.panel.hours")}
              </Link>
              <Link
                to="/dashboard/quality/checklists"
                className="inline-flex items-center justify-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-50 sm:flex-1"
              >
                <ClipboardDocumentCheckIcon className="h-4 w-4" />
                {t("crew.panel.checklists")}
              </Link>
              <button
                type="button"
                onClick={seedChecklists}
                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("crew.panel.seed_checklists")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CrewProjectPanel;
